import { useMemo } from "react";
import { ScrollView, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  formatLocalYmd,
  REPORT_OPTION_TIER,
  type ReportOptionId,
  type ReportRangeAdjustment,
  type ReportSectionId,
  type TimeRange,
} from "@heraion/shared";

import { WidgetConfigRow } from "../Home/WidgetConfigRow";
import { Button } from "../primitives/Button";
import { helpTextStyle } from "../primitives/helpTextStyle";
import { SectionHeader } from "../primitives/SectionHeader";
import { DETAIL_PANE_MAX_WIDTH, useBreakpoint } from "../useBreakpoint";
import { ProgressReportBody } from "./ProgressReportBody";
import { useMaxHistory } from "./ProgressPrTimelineCard";
import { ReportPageCapture } from "./ReportPage";
import { useReportPagePlan } from "./reportPagePlan";
import { ReportPeriodPicker } from "./ReportPeriodPicker";
import type { ProgressDeps } from "./types";
import { useReportPages } from "./useReportPages";
import { useSessionResultsFromDeps } from "./useSessionResults";

type FormatDate = (iso: string, locale: string) => string;

/** Host seam: what happens to the captured pages, PNGs in page order
 *  (mobile share sheet, web share or downloads). `fileStem` names the
 *  period, without extension. */
export type ShareReportImage = (
  uris: string[],
  title: string,
  fileStem: string,
) => Promise<void>;

const ymdOf = (ms: number) => formatLocalYmd(new Date(ms));

// PG-64: the open report: the controls, the report on screen, and its pages
// drawn offscreen for Share. Mounted only while the sheet is open, so the
// period's rows and the MAXes are read only then, once for both drawings.
export function ProgressReportContent({
  deps,
  order,
  adjusted,
  onPick,
  onChooseSections,
  premiumHint,
  formatDate,
  locale,
  onShareImage,
  onShareError,
}: {
  deps: ProgressDeps & { range: TimeRange };
  order: readonly ReportSectionId[];
  adjusted: ReportRangeAdjustment | null;
  onPick: (end: "from" | "to", pickedMs: number) => void;
  onChooseSections: () => void;
  premiumHint: string;
  formatDate: FormatDate;
  locale: string;
  onShareImage?: ShareReportImage;
  onShareError?: (message: string) => void;
}) {
  const { theme, range } = deps;
  const { t } = useTranslation();
  const { wide } = useBreakpoint();
  const styles = makeStyles(theme);

  const withPrs = order.includes("prTimeline");
  const maxHistory = useMaxHistory(deps.apiFetch, withPrs);
  // Same cache as the sections: this joins their read, it adds none.
  const { results, catalog } = useSessionResultsFromDeps(deps);
  // The image waits for what its sections draw, or it would show spinners.
  const ready =
    results !== null && (!withPrs || maxHistory.items !== null || maxHistory.error);

  // A picture has nothing to press: no picker here; the branded body turns
  // off the help pills and gesture hints itself.
  const captureDeps = useMemo(
    () => ({ ...deps, ExercisePickerMount: undefined }),
    [deps],
  );

  const title = t("mobile.progress.report.title", { defaultValue: "Training report" });
  const share = useReportPages(
    useReportPagePlan({ results, catalog, range }, order, maxHistory),
    async (uris) => {
      await onShareImage?.(
        uris,
        title,
        `report-${ymdOf(range.fromMs)}-${ymdOf(range.toMs)}`,
      );
    },
    onShareError,
  );

  const optionLabel: Record<ReportOptionId, string> = {
    pdfExport: t("mobile.progress.report.pdfExport", {
      defaultValue: "Export as PDF",
    }),
    comparePeriods: t("mobile.progress.report.comparePeriods", {
      defaultValue: "Compare two periods",
    }),
  };
  const lockedOptions = (Object.keys(REPORT_OPTION_TIER) as ReportOptionId[]).filter(
    (id) => REPORT_OPTION_TIER[id] === "premium",
  );
  const noSections = order.length === 0;

  return (
    <>
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={[styles.controls, wide && styles.controlsWide]}>
          <ReportPeriodPicker
            theme={theme}
            range={range}
            locale={locale}
            onPick={onPick}
            adjusted={adjusted}
          />
          <View style={styles.actions}>
            <Button
              theme={theme}
              variant="secondary"
              icon="list"
              label={t("mobile.progress.report.chooseSections", {
                defaultValue: "Choose sections",
              })}
              onPress={onChooseSections}
            />
            {onShareImage ? (
              <Button
                theme={theme}
                variant="secondary"
                icon="share"
                label={t("mobile.progress.report.shareImage", {
                  defaultValue: "Share as image",
                })}
                // Loading while the period is read: the spinner says why.
                loading={share.sharing || (!noSections && !ready)}
                // While the pages are drawn it says which: «Preparing image 3 of 9…».
                accessibilityLabel={share.progress ?? undefined}
                disabled={noSections}
                onPress={share.onShare}
              />
            ) : null}
          </View>
          {onShareImage && (noSections || share.progress) ? (
            // Live: the web's only voice for the capture's progress (RNW has
            // no announceForAccessibility; native also gets useReportPages').
            <Text style={styles.hint} accessibilityLiveRegion="polite">
              {share.progress ??
                t("mobile.progress.report.shareNeedsSection", {
                  defaultValue: "Choose at least one section to share the report.",
                })}
            </Text>
          ) : null}
          <View>
            <SectionHeader
              theme={theme}
              title={t("mobile.progress.report.advanced", {
                defaultValue: "Advanced report",
              })}
            />
            <Text style={[styles.hint, styles.hintGap]}>{premiumHint}</Text>
            {lockedOptions.map((id) => (
              <WidgetConfigRow key={id} kind="locked" theme={theme} label={optionLabel[id]} />
            ))}
          </View>
        </View>

        <ProgressReportBody
          deps={deps}
          order={order}
          wide={wide}
          formatDate={formatDate}
          locale={locale}
          maxHistory={maxHistory}
        />
      </ScrollView>

      {share.current ? (
        <ReportPageCapture
          // A new page is a new mount, so its layout is reported again.
          key={share.current.page.index}
          ref={share.cardRef}
          deps={captureDeps}
          run={share.current}
          formatDate={formatDate}
          locale={locale}
          maxHistory={maxHistory}
          onLayout={share.onPageLayout}
        />
      ) : null}
    </>
  );
}

function makeStyles(theme: ProgressDeps["theme"]) {
  const { spacing: s } = theme;
  return StyleSheet.create({
    scroll: { padding: s.s5, paddingTop: s.s2, gap: s.s5 },
    controls: { gap: s.s4 },
    // Wide: the controls keep the detail pane's readable width.
    controlsWide: { maxWidth: DETAIL_PANE_MAX_WIDTH },
    actions: { flexDirection: "row", flexWrap: "wrap", gap: s.s3 },
    hint: helpTextStyle(theme),
    hintGap: { marginBottom: s.s3 },
  });
}
